import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { NewsArticle } from "@/lib/index";

interface NewsCardProps {
  article: NewsArticle;
}

const categoryLabels: Record<NewsArticle['category'], string> = {
  'logistics-trends': 'Logistics Trends',
  'supply-chain': 'Supply Chain',
  'industry-insights': 'Industry Insights',
  'company-news': 'Company News',
};

const categoryColors: Record<NewsArticle['category'], string> = {
  'logistics-trends': 'bg-chart-1 text-white',
  'supply-chain': 'bg-chart-2 text-white',
  'industry-insights': 'bg-chart-3 text-white',
  'company-news': 'bg-chart-4 text-white',
};

export function NewsCard({ article }: NewsCardProps) {
  return (
    <Card className="group overflow-hidden transition-all duration-200 hover:shadow-lg hover:scale-[1.02]">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute top-4 left-4">
          <Badge className={categoryColors[article.category]}>
            {categoryLabels[article.category]}
          </Badge>
        </div>
        {article.featured && (
          <div className="absolute top-4 right-4">
            <Badge variant="secondary" className="bg-accent text-accent-foreground">
              Featured
            </Badge>
          </div>
        )}
      </div>

      <CardHeader className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <time dateTime={article.date}>
            {new Date(article.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
        </div>
        <h3 className="text-xl font-semibold line-clamp-2 group-hover:text-primary transition-colors">
          {article.title}
        </h3>
      </CardHeader>

      <CardContent>
        <p className="text-muted-foreground line-clamp-3">{article.excerpt}</p>
      </CardContent>

      <CardFooter>
        <Button
          variant="ghost"
          className="group/btn p-0 h-auto font-medium text-primary hover:text-primary/80"
          asChild
        >
          <Link to={`/news/${article.id}`}>
            Read More
            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
